import { ArticleType, RootState, useFetchGuestKnowledgeArticleMutation, useFetchKnowledgeArticleMutation } from "@circuitry-ai/doc-data";
import { useEffect, useRef, useState } from "react";
import { convertToLocalDayMonthYear } from "../utils/dateutils";

import { v4 as uuidv4 } from 'uuid';
import { useSelector } from "react-redux";
import { getUserName } from "../utils/commonutils";
import { ArticleModel } from "./articlemodel";
import { ArticleReadOnlyModal } from "./ArticleReadOnlyModal";

interface ArticleLayoutProps {
    articleId?: string;
    messageId?: string;
    sessionId?: string;
    advisorId?: string;
    onClose: () => void;
    refetch?: () => void;
    readOnly?: boolean;
    /** Conversation screens only: knowledge articles open in the trimmed question/answer viewer. */
    conversationView?: boolean;
}


const ArticleLayout = (props: ArticleLayoutProps) => {
    const { articleId, messageId, sessionId, advisorId, onClose, refetch, readOnly = false, conversationView = false } = props;

    const [fetchKnowledgeArticle] = useFetchKnowledgeArticleMutation();
    const [fetchGuestKnowledgeArticle] = useFetchGuestKnowledgeArticleMutation();
    const [articleData, setArticleData] = useState<any>(null);

    const { details: user, isAuthenticated } = useSelector((state: RootState) => state.auth.user);
    const { token: authToken } = useSelector((state: RootState) => state.auth);
    const { guestToken } = useSelector((state: RootState) => state.advisor);
    const userName = getUserName(isAuthenticated, user);

    const fetchRequest = useRef(false);

    const fetchArticle = async () => {
        try {
            const useGuestArticleApi = Boolean(guestToken) && !authToken;
            const response = useGuestArticleApi
                ? await fetchGuestKnowledgeArticle({
                    article_id: articleId ?? '',
                    token: guestToken,
                }).unwrap()
                : await fetchKnowledgeArticle(articleId ?? '').unwrap();
            const articleInitialData = response?.[0];

            if (articleInitialData) {
                setArticleData({
                    question: articleInitialData?.question ?? '',
                    answer: articleInitialData?.answer ?? '',
                    name: articleInitialData?.name ?? '',
                    metadata: articleInitialData?.metadata.length > 0 ? articleInitialData?.metadata : [{ name: '', value: '', id: uuidv4() }],
                    article_type: articleInitialData?.type,
                    article_status: articleInitialData?.status,
                    advisor_id: articleInitialData?.advisor_id,
                    author: articleInitialData?.created_by,
                    upload_date: convertToLocalDayMonthYear(articleInitialData?.created_at).split(',')[0],
                    article_id: articleInitialData?.article_id,
                    sender_id: sessionId,
                    message_id: messageId,
                })
            }
        } catch (error) {
            console.log(error);
        }
    }


    useEffect(() => {

        if (fetchRequest.current) return;
        fetchRequest.current = true;
        if (articleId) fetchArticle();
        else {
            setArticleData({
                question: '',
                answer: '',
                name: '',
                metadata: [{ name: '', value: '', id: uuidv4() }],
                article_type: ArticleType.KNOWLEDGE_ARTICLE,
                article_status: '',
                advisor_id: advisorId ?? '',
                author: userName ?? '',
                upload_date: convertToLocalDayMonthYear(new Date().toISOString()).split(',')[0],
                article_id: '',
                sender_id: sessionId,
                message_id: messageId,
            });
        }

    }, [articleId]);

    const handleClose = () => {
        setArticleData(null);
        onClose();
    }


    // Knowledge articles opened from a conversation are the only case for the trimmed viewer;
    // every other caller (knowledge screen, expert answers) keeps the full article modal.
    const showConversationViewer = conversationView && articleData?.article_type === ArticleType.KNOWLEDGE_ARTICLE;

    return (
        <div>
            {articleData && (
                showConversationViewer ? (
                    <ArticleReadOnlyModal
                        onClose={handleClose}
                        articleData={articleData}
                    />
                ) : (
                    <ArticleModel
                        onClose={handleClose}
                        articleData={articleData}
                        refetch={refetch}
                        readOnly={readOnly}
                    />
                )
            )}
        </div>
    )
}

export { ArticleLayout };